import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BranchingQueryDefinition, DeliveryType, Ruleset, RulesetClassName } from '@iotv/datamodel';
import { Alert, Stack, Typography } from '@mui/material';
import { useEffect, useReducer, useRef, useState } from 'react';
import config from '../../../config';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { CustomerDAO } from '../../../data/SystemManagementDAO';
import { isAdminRole } from '../../../helpers/access';
import { getListing } from '../../../helpers/format';
import { getVisibleGroups } from '../../../helpers/queryHelpers';
import { AdjacentType } from '../../../types';
import { AlertManagementContext, AlertManagementContextDefault, AlertManagementContextReducer } from './AlertManagementContext';
import { GlobalRuleset } from './GlobalRuleset';
import { SystemRulesets } from './SystemRulesets';
import { UserSelector } from './UserSelector';
const debug = process.env.REACT_APP_DEBUG && false;
const appCode = config.app.appCode.substring(0, 4);
export const options = {
    mismatchMessage: (_, mismatched) => {
        return [`Mismatched — ${getListing(mismatched, 'all')} not matching!`, 'Update'];
    },
    subscriptionFilters: () => [],
    subscriptionDeliveryTypes: () => [DeliveryType.EMAIL, DeliveryType.MOBILE_PUSH],
    allowThresholdAdjustment: () => false,
};
import(`../../../venture/${appCode}/components/AlertManagement/options`)
    .then((module) => {
    Object.assign(options, module.options);
    debug && console.log('AlertManagement loaded venture-specific alert options:', module.options);
})
    .catch((e) => {
    debug && console.log('No venture-specific alert options, using default options...', e.message);
});
export const AlertManagement = ({ contextCustomer, user, userSelectedGroup }) => {
    const [context, dispatch] = useReducer(AlertManagementContextReducer, AlertManagementContextDefault);
    const dispatchRef = useRef(dispatch);
    const { contextUser, fetchingSeedCustomer, seedCustomer, fetchingGlobalRulesets, globalRulesets, fetchingLocalRulesets, localRulesets } = context;
    const loaded = seedCustomer !== undefined &&
        contextCustomer !== undefined &&
        contextUser !== undefined &&
        !fetchingSeedCustomer &&
        !fetchingGlobalRulesets &&
        !fetchingLocalRulesets;
    // eslint-disable-next-line react-hooks/immutability
    context.loaded = loaded;
    const optionsContext = {
        contextCustomer,
        user,
        userSelectedGroup,
        ...context,
    };
    const [showSystemRulesets, setShowSystemRulesets] = useState(false);
    // Fetch the seed customer
    useEffect(() => {
        if (seedCustomer?.sk)
            return;
        dispatch({ fetchingSeedCustomer: true });
        ConsumerDao.getNamedApexItems({ type: 'Customer' }).then(({ err, data }) => {
            dispatch({ fetchingSeedCustomer: false });
            if (err || !data) {
                debug && console.log('AlertManagement getNamedApexItems', err, data);
            }
            else {
                const c = data.shift();
                if (c)
                    dispatch({ seedCustomer: c });
            }
        });
    }, [seedCustomer?.sk]);
    // Dispatch the context customer
    useEffect(() => {
        dispatch({ contextCustomer });
    }, [contextCustomer?.sk]);
    // Dispatch the context user
    useEffect(() => {
        dispatch({ contextUser: user, visibleGroups: [] });
        if (user) {
            getVisibleGroups(user).then((r) => {
                if (r.data) {
                    dispatch({ visibleGroups: r.data });
                }
            });
        }
    }, [user?.sk]);
    useEffect(() => {
        if (!user || !contextCustomer)
            return;
        dispatch({ contextUserIsAdmin: false });
        CustomerDAO.canAdminCustomer({ contextUser: user, customer: contextCustomer }).then((canAdmin_res) => {
            if (canAdmin_res.data) {
                dispatch({ contextUserIsAdmin: true });
            }
        });
    }, [contextCustomer?.sk, user?.sk]);
    // Fetch global rulesets adjacent to the seed customer (true global)
    useEffect(() => {
        if (!seedCustomer)
            return;
        dispatch({ fetchingGlobalRulesets: true });
        ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [seedCustomer],
            query: new BranchingQueryDefinition({
                id: seedCustomer.sk,
                path: [{ objectTypeId: RulesetClassName, adjacencyType: AdjacentType.CHILD }],
                subQueries: [],
            }),
        }).then(({ err, data }) => {
            dispatch({ fetchingGlobalRulesets: false });
            if (err || !data) {
                debug && console.log('AlertManagement getGlobalRulesets', err, data);
            }
            else {
                dispatch({
                    globalRulesets: data
                        .getNodesOfType(RulesetClassName)
                        .map((r) => new Ruleset(r))
                        .filter((r) => r.global)
                        .sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0),
                });
            }
        });
    }, [seedCustomer?.sk]);
    // Fetch global rulesets adjacent to the context customer (local)
    useEffect(() => {
        if (!contextCustomer)
            return;
        dispatch({ fetchingLocalRulesets: true });
        ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: new BranchingQueryDefinition({
                id: contextCustomer.sk,
                path: [{ objectTypeId: RulesetClassName, adjacencyType: AdjacentType.CHILD }],
                subQueries: [],
            }),
        }).then(({ err, data }) => {
            dispatch({ fetchingLocalRulesets: false });
            if (err || !data) {
                debug && console.log('AlertManagement getLocalRulesets', err, data);
            }
            else {
                dispatch({
                    localRulesets: data
                        .getNodesOfType(RulesetClassName)
                        .map((r) => new Ruleset(r))
                        .sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0),
                });
            }
        });
    }, [contextCustomer?.sk]);
    useEffect(() => {
        if (contextCustomer && user && contextUser && user.sk === contextUser.sk) {
            CustomerDAO.isUserForCustomer({ contextUser, customer: contextCustomer }).then(({ err, data }) => {
                if (err || data === null)
                    throw new Error("Likely missing the 'isUserForCustomer' endpoint, please deploy the System Management API! \
            Alternatively, the user may not have permission to access the Customer System Management API endpoint(s).");
                setShowSystemRulesets(data);
            });
        }
        else {
            setShowSystemRulesets(false);
        }
        if (contextCustomer && user && isAdminRole(userSelectedGroup)) {
            CustomerDAO.canAdminCustomer({ contextUser: user, customer: contextCustomer }).then(({ err, data }) => {
                if (err || data === null)
                    return;
                dispatch({ isAdmin: data });
            });
        }
        else {
            dispatch({ isAdmin: false });
        }
    }, [contextCustomer?.sk, user?.sk, contextUser?.sk, userSelectedGroup]);
    return (_jsx(AlertManagementContext.Provider, { value: [context, dispatchRef.current], children: _jsxs(Stack, { padding: 2, alignItems: 'flex-start', gap: 1, children: [_jsxs(Stack, { direction: 'row', flexWrap: 'wrap', gap: 1, children: [_jsx(Typography, { variant: "h5", children: "Alert Management" }), _jsx(UserSelector, {})] }), showSystemRulesets && _jsx(SystemRulesets, {}, contextUser?.sk), _jsx(_Fragment, { children: seedCustomer !== undefined && seedCustomer.sk !== contextCustomer?.sk ? (_jsxs(_Fragment, { children: [globalRulesets.map((g) => {
                                const l = localRulesets.find((l) => g.name === l.name);
                                return _jsx(GlobalRuleset, { ...optionsContext, global: g, local: l }, g.id);
                            }), localRulesets
                                .filter((l) => l.global)
                                .map((l) => {
                                const orphan = !globalRulesets.find((g) => g.name === l.name);
                                if (!orphan)
                                    return;
                                return _jsx(GlobalRuleset, { ...optionsContext, local: l }, l?.id);
                            }), globalRulesets.length === 0 && localRulesets.filter((l) => l.global).length === 0 && _jsx(Alert, { severity: "warning", children: "No rulesets to manage!" })] })) : (globalRulesets.map((g) => {
                        const l = localRulesets.find((l) => g.name === l.name);
                        return _jsx(GlobalRuleset, { ...optionsContext, global: g, local: l }, g.id);
                    })) })] }) }));
};
